<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="modal-overlay"
        :style="stackZIndex ? { zIndex: stackZIndex } : undefined"
        @click="handleOverlayClick"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <div
          class="modal-content overflow-hidden flex flex-col"
          :class="[sizeClasses, contentClasses]"
          @click.stop
        >
          <!-- Header -->
          <div v-if="$slots.header || title" class="shrink-0 flex items-center justify-between px-6 py-5 border-b" :class="headerFooterClasses">
            <div>
              <slot name="header">
                <h3 v-if="title" :id="titleId" class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {{ title }}
                </h3>
                <p v-if="description" class="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {{ description }}
                </p>
              </slot>
            </div>
            <button
              v-if="closable"
              @click="close"
              class="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800"
              aria-label="Close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 py-5">
            <slot />
          </div>

          <!-- Footer -->
          <div v-if="$slots.footer" class="shrink-0 px-6 py-4 border-t" :class="headerFooterClasses">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { openModalStack } from '../../composables/modalStack'

interface Props {
  open?: boolean
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '4.5xl' | '5xl' | '6xl' | '7xl' | '8xl' | 'full'
  closable?: boolean
  closeOnOverlayClick?: boolean
  closeOnEsc?: boolean
  // Sem efeito — mantida só por compatibilidade (ver headerFooterClasses).
  variant?: 'default' | 'gray'
  // Altura fixa no mobile (abaixo de md) — pra formulários em etapas, em que
  // cada etapa tem uma altura e o rodapé muda de botões: sem isso o modal
  // encolhe/estica a cada "Próximo". O corpo (flex-1, com rolagem) absorve a
  // diferença. Ver o cadastro de serviço em Services.vue.
  fixedHeightMobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  size: 'md',
  closable: true,
  closeOnOverlayClick: true,
  closeOnEsc: true,
  variant: 'default',
})

// Paleta única (zinc, neutro) pra todo modal. A variante 'gray' começou como
// teste de paleta e virou o padrão do app; o slate azulado que a variante
// 'default' usava no dark mode foi removido. A prop variant segue aceita
// (vários usos passam variant="gray"), mas as duas renderizam igual.
const headerFooterClasses = 'border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900'

// .modal-content usa bg-card (global, style.css) — !important pra garantir
// que o zinc ganhe da classe component (ordem das camadas do Tailwind não é
// garantida aqui).
const contentClasses = computed(() => [
  'dark:!bg-zinc-900 dark:!border-zinc-800',
  props.fixedHeightMobile ? 'max-md:h-[90dvh]' : '',
])

const emit = defineEmits<{
  close: []
  'update:open': [value: boolean]
}>()

const titleId = computed(() => `modal-title-${Math.random().toString(36).substr(2, 9)}`)

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '4.5xl': 'max-w-[960px]',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    '8xl': 'max-w-7xl',
    full: 'max-w-full',
  }
  return sizes[props.size]
})

const close = () => {
  emit('update:open', false)
  emit('close')
}

const handleOverlayClick = () => {
  if (props.closeOnOverlayClick) {
    close()
  }
}

// Handle ESC key — com modais aninhados (ex: Detalhe de Venda aberto por
// cima do modal de Produto, ver Vendas.vue), só o modal do topo da pilha
// deve fechar; sem isso, Esc fecharia todos os modais abertos de uma vez,
// já que cada instância registra seu próprio listener no document.
let escToken: symbol | null = null
const handleEsc = (e: KeyboardEvent) => {
  if (e.key !== 'Escape' || !props.closable || !props.closeOnEsc) return
  if (openModalStack[openModalStack.length - 1] !== escToken) return
  close()
}

// z-index pela posição na pilha — o modal aberto por último fica sempre por
// cima (ex: confirmação de exclusão aberta a partir do modal de edição).
// Sem isso todos ficam em z-50 (.modal-overlay) e o de cima dependia só da
// ordem dos teleports no <body>, que não acompanha a ordem de abertura.
const stackZIndex = ref<number | null>(null)

watch(() => props.open, (newOpen) => {
  if (newOpen) {
    escToken = Symbol()
    openModalStack.push(escToken)
    stackZIndex.value = 50 + openModalStack.length
    document.addEventListener('keydown', handleEsc)
  } else {
    document.removeEventListener('keydown', handleEsc)
    if (escToken) {
      const idx = openModalStack.indexOf(escToken)
      if (idx !== -1) openModalStack.splice(idx, 1)
    }
    escToken = null
  }
})

// Prevent body scroll when modal is open
watch(() => props.open, (newOpen) => {
  if (newOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.2s ease-out;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.95);
}
</style>